import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { PromptTypes } from 'generated/graphql-types';
import { EMPTY, Observable, of } from 'rxjs';
import {
   catchError,
   concatMap,
   exhaustMap,
   filter,
   map,
   mergeMap,
   switchMap,
   tap,
   withLatestFrom,
} from 'rxjs/operators';
import { extensionsRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import {
   getCurrentUser,
   hasSelectedPrivilege,
   selectPrivileges,
} from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { DeleteExtensionDialogComponent } from 'src/app/common-components/delete-extension-dialog/delete-extension-dialog.component';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { ExtensionCallQueueHubService } from 'src/app/signalr/extension-call-queue-hub/extension-call-queue-hub.service';
import { getSelectedAccount, getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { AddExistingPromptComponent } from '../../add-existing-prompt/containers/add-existing-prompt.component';
import { AddFindMeFollowMeStepComponent } from '../../add-find-me-follow-me-step/containers/add-find-me-follow-me-step.component';
import { AddPromptComponent } from '../../add-prompt/containers/add-prompt.component';
import * as AddPromptActions from '../../add-prompt/state/add-prompt.actions';
import { AddTimeBasedRouteComponent } from '../../add-time-based-route/containers/add-time-based-route.component';
import { EditMailboxPinComponent } from '../../edit-mailbox-pin/containers/edit-mailbox-pin.component';
import { ExtensionRegisterApiService } from '../../extension-register/services/extension-register-api.service';
import { VoiceMailGreetingPromptOption } from '../../mailbox-details/models/voicemail-greeting-prompt-option.model';
import { MailboxDetailApiService } from '../../mailbox-details/services/mailbox-detail-api.service';
import * as ExtensionPhoneRegisterActions from '../../phones/extension-phone-register/state/extension-phone-register.actions';
import { ExtensionPhoneHubService } from 'src/app/signalr/extension-phone-hub/extension-phone-hub.service';
import { PromptOption } from '../../prompt-register/models/prompt-option.interface';
import { ChangeDNDStatusComponent } from '../containers/change-dnd-status/containers/change-dnd-status.component';
import { getHackedFraudDetails } from '../helpers/hacked-fraud-helper';
import { ExtensionDetailSaveModel, ExtensionPhoneSaveModel } from '../models/extension-detail-save.model';
import { ExtensionDetailView } from '../models/extension-detail-view.model';
import { getExtensionDetail } from '../queries/extension-detail.graphql';
import { ExternalNumbersQuery } from '../queries/external-numbers.graphql';
import {
   ExternalNumberHistories,
   ExternalNumberHistoriesVariables,
} from '../queries/generated/ExternalNumberHistories';
import { GetExtensionDetail, GetExtensionDetailVariables } from '../queries/generated/GetExtensionDetail';
import { ExtensionDetailApiService } from '../services/extension-detail-api.service';
import * as ExtensionDetailActions from './extension-detail.actions';
import {
   changeSipUserPassword,
   changeSipUserPasswordFailure,
   changeSipUserPasswordSuccess,
} from './extension-detail.actions';
import { getExtension, getExtensionAccountId } from './extension-detail.selectors';
import { extensionPhoneForwardCallsChanged } from 'src/app/signalr/extension-phone-hub/extension-phone-hub.actions';

@Injectable()
export class ExtensionDetailEffects {
   dialogId: string;

   loadExtensionDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            ExtensionDetailActions.extensionDetailLoad,
            ExtensionDetailActions.saveExtensionDetailSuccess,
            ExtensionDetailActions.extensionDetailCancel,
            ExtensionDetailActions.changeSipUserPasswordSuccess,
            ExtensionPhoneRegisterActions.changeSipUserPasswordSuccess,
            ExtensionDetailActions.logAgentOutOfQueueSuccess
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         concatMap(([action, params]) => {
            const id = parseInt(params.id, 10);
            return this.apiService
               .query<GetExtensionDetail, GetExtensionDetailVariables>({
                  query: getExtensionDetail,
                  variables: {
                     id,
                  },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([extensionResult, privileges, route]) => {
                     const extensionIsArchived = extensionResult.data.extension.isArchived;
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageExtensions_Enabled,
                           extensionResult.data.extension.account?.accountId ?? 0,
                           privileges
                        ) ||
                        extensionIsArchived
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return ExtensionDetailActions.loadExtensionAccessDenied();
                     }
                     return ExtensionDetailActions.loadExtensionDetailSuccess({
                        data: extensionResult.data.extension,
                     });
                  }),
                  catchError((error) => {
                     return of(ExtensionDetailActions.loadExtensionDetailFailure(error));
                  })
               );
         })
      );
   });

   loadExternalNumbers = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            ExtensionDetailActions.extensionDetailLoad,
            ExtensionDetailActions.saveExtensionDetailSuccess,
            ExtensionDetailActions.extensionDetailCancel,
            ExtensionDetailActions.changeSipUserPasswordSuccess,
            ExtensionPhoneRegisterActions.changeSipUserPasswordSuccess
         ),
         withLatestFrom(
            this.store.pipe(select(selectRouteParams)),
            this.store.pipe(select(getCurrentUser)),
            this.store.pipe(select(getSelectedAccount))
         ),
         concatMap(([action, params, currentUser, account]) => {
            let filters = 'AccountId = ' + account.accountId;
            return this.apiService
               .query<ExternalNumberHistories, ExternalNumberHistoriesVariables>({
                  query: ExternalNumbersQuery,
                  variables: {
                     filters: filters,
                  },
               })
               .pipe(
                  map((result) => {
                     return ExtensionDetailActions.loadExternalNumbersSuccess({
                        data: result.data,
                     });
                  }),
                  catchError((error) => {
                     return of(ExtensionDetailActions.loadExternalNumbersFailure(error));
                  })
               );
         })
      );
   });

   loadExtensionNumbers$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.loadExtensionDetailSuccess, ExtensionDetailActions.saveExtensionDetailSuccess),
         withLatestFrom(this.store.pipe(select(getExtensionAccountId))),
         switchMap(([action, accountId]) => {
            const id = accountId ? accountId : null;
            let currentExtensionNumber: number;
            if (action.type === ExtensionDetailActions.saveExtensionDetailSuccess.type) {
               currentExtensionNumber = action.data.extension.number;
            } else if (action.type === ExtensionDetailActions.loadExtensionDetailSuccess.type) {
               currentExtensionNumber = action.data.number;
            }
            return this.extensionRegisterApiService.getExtensionNumbers(null, id).pipe(
               map((result) => {
                  const data = result.data.extensionsConnection.items || [];
                  const extDetails = data.map((item) => ({
                     type: item.type,
                     extensionId: item.extensionId,
                     number: item.number,
                  }));
                  return ExtensionDetailActions.loadAccountExtensionsSuccess({
                     data: extDetails,
                  });
               }),
               catchError((error) => {
                  console.error('Error fetching extension numbers:', error);
                  return EMPTY;
               })
            );
         })
      );
   });

   changeSipUserPassword$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(changeSipUserPassword),
         switchMap((action) => {
            return this.extensionDetailApiService.changeSipUserPasswords(action.sipUserIds).pipe(
               map(() => {
                  return changeSipUserPasswordSuccess();
               }),
               catchError((error) => {
                  return of(changeSipUserPasswordFailure(error));
               })
            );
         })
      );
   });

   getHackedFraudDetails = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.loadExtensionDetailSuccess),
         map((action) => {
            const details = getHackedFraudDetails(action.data);
            return ExtensionDetailActions.getHackedFraudDetailsSuccess({ details });
         })
      );
   });
   saveExtensionDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.saveExtensionDetail),
         withLatestFrom(this.store.pipe(select(getExtension))),
         switchMap(([action, extension]) => {
            let modifiedExtension: ExtensionDetailSaveModel | undefined = undefined;
            if (extension) {
               modifiedExtension = this.setNewPhoneIdsToZero(action.extension, extension);
            }

            const apiCall = this.extensionDetailApiService.save(
               modifiedExtension ?? action.extension,
               action.extensionFields,
               action.extensionPhoneFields,
               action.extensionAutoAttendantFields,
               action.extensionExternalForwardFields,
               action.extensionHuntGroupFields,
               action.extensionMailboxFields,
               action.mailboxFields,
               action.extensionQueueFields,
               action.extensionFaxFields,
               action.extensionFindMeFollowMeFields,
               action.extensionPagingGroupFields,
               extension
            );

            return apiCall.pipe(
               map((result) => {
                  return ExtensionDetailActions.saveExtensionDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(ExtensionDetailActions.saveExtensionDetailFailure(error));
               })
            );
         })
      );
   });

   saveExtensionDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.saveExtensionDetailSuccess),
            tap(() => {
               this.snackBar.open('Extension Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   loadExtensionMailbox = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.loadExtensionMailbox),
         withLatestFrom(this.store.pipe(select(getExtension))),
         switchMap(([_, extension]) => {
            if (extension?.mailbox?.mailboxId) {
               return this.mailboxDetailApiService.get(extension?.mailbox?.mailboxId).pipe(
                  map((result) => {
                     return ExtensionDetailActions.loadExtensionMailboxSuccess({ mailbox: result.data.mailbox });
                  }),
                  catchError((error) => {
                     return of(ExtensionDetailActions.loadExtensionMailboxFailure(error));
                  })
               );
            } else {
               return EMPTY;
            }
         })
      );
   });

   registerAsViewingExtensionPhones$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.extensionDetailLoad),
            withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
            tap(([_, accountId]) => {
               this.extensionCallQueueHubService.registerExtensionQueueIsLoggedInEvents(accountId);
               this.extensionPhoneHubService.registerExtensionPhoneForwardCallsEvents(accountId);
            })
         );
      },
      { dispatch: false }
   );

   refreshOnForwardCallsChanged$ = createEffect(() => {
   return this.actions$.pipe(
      ofType(extensionPhoneForwardCallsChanged),
      withLatestFrom(this.store.pipe(select(getExtension))),
      filter(([action, extension]) => {
         return extension?.extensionPhone?.extensionPhoneId === action.extensionPhoneId;
      }),
      map(() => ExtensionDetailActions.extensionDetailLoad())
   );
});


   deleteExtension = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.deleteExtension),
         switchMap((action) => {
            const dialogRef = this.dialogService.openFormComponent(DeleteExtensionDialogComponent, {
               closeOnNavigation: true,
               data: {
                  autoAttendantsUsingForTimeout: action.autoAttendantsUsingForTimeout,
                  fmFmsUsingForFinalDestination: action.fmFmsUsingForFinalDestination,
                  timeBasedRoutesUsingForRoute: action.timeBasedRoutesUsingForRoute,
                  queuesUsingForExitRoute: action.queuesUsingForExitRoute,
                  huntGroupsUsingForRouteBusy: action.huntGroupsUsingForRouteBusy,
                  huntGroupsUsingForRouteNoAnswer: action.huntGroupsUsingForRouteNoAnswer,
               },
               disableClose: true,
               panelClass: ['dialog-md', 'dialog-container'],
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.extensionDetailApiService.deleteExtension(action.extensionId).pipe(
                        map(() => {
                           return ExtensionDetailActions.deleteExtensionSuccess(action);
                        }),
                        catchError((error) => {
                           return of(ExtensionDetailActions.deleteExtensionFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteExtensionSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.deleteExtensionSuccess),
            tap((action) => {
               this.router.navigate([extensionsRoute]);
               this.snackBar.open(`Extension ${action.number} deleted`);
            })
         );
      },
      { dispatch: false }
   );

   addExistingPrompt = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.addExistingPrompt),
         withLatestFrom(this.store.pipe(select(getExtension))),
         switchMap(([action, extension]) => {
            return this.openExistingPromptDialog(extension?.accountId, action.promptTypes);
         })
      );
   });

   addNewPrompt = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.addNewPrompt),
            withLatestFrom(this.store.pipe(select(getExtension))),
            switchMap(([action, extension]) => {
               return this.openNewPromptDialog(
                  extension?.accountId,
                  action?.promptType,
                  action.voiceMailGreetingPrompt
               );
            })
         );
      },
      { dispatch: false }
   );

   addNewPromptEffect = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.createNewFromExtensionPromptSuccess),
         filter((action) => {
            return !!action?.prompt?.prompt;
         }),
         map((action) => {
            const promptOption: PromptOption = {
               ...action.prompt.prompt,
               voiceMailGreetingPrompt: action.voiceMailGreetingPrompt,
            };
            return ExtensionDetailActions.promptItemDialogSubmit({ promptItem: promptOption });
         })
      );
   });

   recordNewPromptEffect = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.recordNewFromExtensionPromptSuccess),
         filter((action) => {
            return !!action?.data;
         }),
         map((action) => {
            const promptOption: PromptOption = {
               ...action.data,
               voiceMailGreetingPrompt: action.voiceMailGreetingPrompt,
            };
            return ExtensionDetailActions.promptItemDialogSubmit({ promptItem: promptOption });
         })
      );
   });

   addNewTimeBasedRoute = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.addNewTimeBasedRoute),
         withLatestFrom(this.store.pipe(select(getExtension))),
         switchMap(([_, extension]) => {
            return this.openNewTimeBasedRouteDialog(extension);
         })
      );
   });

   addNewFindMeFollowMeStep = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.addNewFindMeFollowMeStep),
         withLatestFrom(this.store.pipe(select(getExtension))),
         switchMap(([_, extension]) => {
            return this.openNewFindMeFollowMeStepDialog(extension);
         })
      );
   });

   addPrompt$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.uploadMohPrompt),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
                  data: {
                     includeReusableOption: true,
                     includedPromptTypes: ['MusicOnHold'],
                     routeToNewPromptAfterSave: false,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(
                  filter((promptItem) => {
                     return promptItem !== undefined;
                  }),
                  map((promptItem) => {
                     return ExtensionDetailActions.promptItemDialogSubmit({ promptItem });
                  })
               );
            })
         );
      },
      { dispatch: false }
   );

   editMailboxPin = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.editMailboxPin),
            withLatestFrom(this.store.pipe(select(getExtension))),
            switchMap(([action, extension]) => {
               return this.openEditMailboxPinDialog(extension?.mailbox?.mailboxId);
            })
         );
      },
      { dispatch: false }
   );

   changeDNDStatus = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.changeDNDStatus),
         switchMap((action) =>
            this.openEditDNDStatusDialog(action.dndStatus).pipe(
               mergeMap((dndStatus) => {
                  if (dndStatus) {
                     return of(ExtensionDetailActions.changeDNDStatusSuccess(dndStatus));
                  }
                  return EMPTY;
               })
            )
         )
      );
   });

   logAgentOutOfQueue$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.logAgentOutOfQueue),
         switchMap((action) => {
            const message = `Are you sure you would like to log out agent ${action.extensionName}?`;

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Log Out Agent?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.extensionDetailApiService.logAgentOutOfQueue(action.queueMemberId).pipe(
                        map(() => {
                           return ExtensionDetailActions.logAgentOutOfQueueSuccess({
                              extensionName: action.extensionName,
                           });
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   logAgentOutOfQueueSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.logAgentOutOfQueueSuccess),
            tap((data) => {
               this.snackBar.open(`Agent ${data.extensionName} Logged Out`);
            })
         );
      },
      { dispatch: false }
   );

   addCallbackPrompt$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.uploadCallbackPrompt),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
                  data: {
                     includeReusableOption: true,
                     includedPromptTypes: ['QueueCallback'],
                     routeToNewPromptAfterSave: false,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(
                  filter((promptItem) => {
                     return promptItem !== undefined;
                  }),
                  map((promptItem) => {
                     return ExtensionDetailActions.promptItemDialogSubmit({ promptItem });
                  })
               );
            })
         );
      },
      { dispatch: false }
   );

   resetMwi = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.resetMwi),
         withLatestFrom(this.store.pipe(select(selectCurrentRoute))),
         switchMap(([action, route]) => {
            let message = 'Are you sure you want to Reset MWI';

            if (route.url[0].path == 'extensions') {
               message += ` for this extension?`;
            } else if (route.url[0].path == 'accountsettings') {
               message += ` for every extension on this account?`;
            } else if (route.url[0].path == 'accounts') {
               message += ` for every extension on all accounts?`;
            } else {
               message += `?`;
            }

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Reset MWI?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return of(
                        ExtensionDetailActions.resetMwiConfirmation({
                           extensionId: action.extensionId,
                           accountId: action.accountId,
                        })
                     );
                  } else {
                     return of(ExtensionDetailActions.resetMwiCancel());
                  }
               })
            );
         })
      );
   });

   resetMwiConfirmation = createEffect(() => {
      return this.actions$.pipe(
         ofType(ExtensionDetailActions.resetMwiConfirmation),
         switchMap((action) => {
            return this.extensionDetailApiService.resetMwi(action.accountId, action.extensionId).pipe(
               map(() => {
                  return ExtensionDetailActions.resetMwiSuccess();
               }),
               catchError((error) => {
                  return of(ExtensionDetailActions.resetMwiFailure());
               })
            );
         })
      );
   });

   resetMwiNotifications = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(ExtensionDetailActions.resetMwiSuccess, ExtensionDetailActions.resetMwiFailure),
            tap((action) => {
               if (action.type === ExtensionDetailActions.resetMwiSuccess.type) {
                  this.snackBar.open('MWI Reset');
               } else if (action.type === ExtensionDetailActions.resetMwiFailure.type) {
                  this.snackBar.open('Error resetting MWI');
               }
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private extensionDetailApiService: ExtensionDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private dialogService: LcsDialogService,
      private location: Location,
      private mailboxDetailApiService: MailboxDetailApiService,
      private extensionRegisterApiService: ExtensionRegisterApiService,
      private extensionCallQueueHubService: ExtensionCallQueueHubService,
      private extensionPhoneHubService: ExtensionPhoneHubService
   ) {}

   openExistingPromptDialog(accountId: number | null | undefined, promptTypes: PromptTypes[]): Observable<Action> {
      const dialogRef = this.dialogService.openFormComponent(AddExistingPromptComponent, {
         closeOnNavigation: true,
         data: {
            accountId,
            promptTypesToInclude: promptTypes,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });

      return dialogRef.afterClosed().pipe(
         filter((promptItem) => {
            return promptItem !== undefined;
         }),
         map((promptItem) => {
            return ExtensionDetailActions.promptItemDialogSubmit({ promptItem });
         })
      );
   }

   openNewPromptDialog(
      accountId: number | null | undefined,
      promptType: PromptTypes,
      voiceMailGreetingPrompt: VoiceMailGreetingPromptOption | null
   ): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
         closeOnNavigation: true,
         data: {
            accountId,
            creatingNewFromExtension: true,
            promptTypeId: promptType,
            voiceMailGreetingPrompt: voiceMailGreetingPrompt,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }

   openNewTimeBasedRouteDialog(extension: ExtensionDetailView | null): Observable<Action> {
      const dialogRef = this.dialogService.openFormComponent(AddTimeBasedRouteComponent, {
         closeOnNavigation: true,
         data: {
            extension: extension,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed().pipe(
         filter((timeBasedRouteItem) => {
            return timeBasedRouteItem !== undefined;
         }),
         map((timeBasedRouteItem) => {
            return ExtensionDetailActions.timeBasedRouteItemDialogSubmit({ timeBasedRouteItem: timeBasedRouteItem });
         })
      );
   }

   openNewFindMeFollowMeStepDialog(extension: ExtensionDetailView | null): Observable<Action> {
      const dialogRef = this.dialogService.openFormComponent(AddFindMeFollowMeStepComponent, {
         closeOnNavigation: true,
         data: {
            extension: extension,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed().pipe(
         filter((fmfmStepItem) => {
            return fmfmStepItem !== undefined;
         }),
         map((findMeFollowMeStepItem) => {
            return ExtensionDetailActions.findMeFollowMeStepSubmit({
               findMeFollowMeStepItem: findMeFollowMeStepItem,
            });
         })
      );
   }

   setNewPhoneIdsToZero(
      extension: ExtensionDetailSaveModel,
      existingExtension: ExtensionDetailView
   ): ExtensionDetailSaveModel | undefined {
      let modifiedExtension = { ...extension };
      const modifiedPhones = modifiedExtension.extension.extensionPhone?.phones?.map((phone) => {
         const existingPhone = existingExtension?.extensionPhone?.phonesConnection.items.find((existingPhone) => {
            return existingPhone.phoneId === phone.phoneId;
         });
         if (!existingPhone) {
            return { ...phone, phoneId: 0 };
         }
         return phone;
      });

      if (modifiedPhones) {
         modifiedExtension = {
            ...modifiedExtension,
            extension: {
               ...modifiedExtension.extension,
               extensionPhone: {
                  ...(modifiedExtension.extension.extensionPhone as ExtensionPhoneSaveModel),
                  phones: modifiedPhones,
               },
            },
         };
      }
      return modifiedExtension;
   }

   openEditMailboxPinDialog(mailboxId: number | null | undefined): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(EditMailboxPinComponent, {
         closeOnNavigation: true,
         data: {
            mailboxId,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }

   openEditDNDStatusDialog(dndStatus: string | null): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(ChangeDNDStatusComponent, {
         closeOnNavigation: true,
         data: {
            dndStatus,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }
}
